import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    CircularProgress
} from '@mui/material';

interface ChooseDocumentNameDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (newName: string) => void;
    originalName: string;
    existingNames: string[];
}

export const ChooseDocumentNameDialog: React.FC<ChooseDocumentNameDialogProps> = ({
    open,
    onClose,
    onConfirm,
    originalName,
    existingNames
}) => {
    const [newName, setNewName] = useState(`${originalName} - Copy`);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (open && originalName) {
          let baseName = `${originalName} - Copy`;
          let counter = 1;
          let newName = baseName;

          // Keep appending numbers until we find a unique name
          while (existingNames.includes(newName)) {
            newName = `${baseName} (${counter})`;
            counter++;
          }

          setNewName(newName);
          setError(null);
          setIsSubmitting(false);
        }
      }, [open, originalName, existingNames]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewName(value);
        
        if (!value.trim()) {
            setError('Document name cannot be empty');
        } else if (existingNames.includes(value)) {
            setError('A document with this name already exists');
        } else {
            setError(null);
        }
    };

    const handleConfirm = async () => {
        if (!error && newName.trim()) {
            setIsSubmitting(true);
            onConfirm(newName.trim());
            onClose();
        }
    };

    return (
        <Dialog
          open={open}
          onClose={(_event, reason) => {
            if (reason === 'backdropClick') return;
            onClose();
          }}
          maxWidth="sm"
          fullWidth
        >
            <DialogTitle>Rename Document</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    A document with the name "{originalName}" already exists. Please choose a different name.
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Document Name"
                    type="text"
                    fullWidth
                    value={newName}
                    onChange={handleNameChange}
                    error={!!error}
                    helperText={error}
                    disabled={isSubmitting}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button 
                    onClick={handleConfirm} 
                    color="primary" 
                    disabled={!!error || !newName.trim() || isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                    {isSubmitting ? 'Loading...' : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 